import { useEffect, useRef, useState } from 'react';
import { MenuButton, MenuGroup, MenuLink, MenuSeparator } from '@frameio/vapor';
import {
  IconCustomFieldLink,
  IconMailRead,
  IconNotifications,
  IconNotificationsDisabled,
} from '@frameio/vapor-icons';
import { useProjectNotificationsStatusFragment } from '../../../gql';
import { useToggleProjectNotifications } from '../../../hooks';
import type {
  NotificationsActionCallback,
  NotificationsActionContext,
} from '../types';
import TEXT from './generated/TEXT/ContextMenuContent';

TEXT.DEFINE({
  /** Menu item to mark notification as read */
  markAsRead: 'Mark as Read',
  /** Menu item to mark notification as unread */
  markAsUnread: 'Mark as Unread',
  /** Menu item to mute project notifications */
  disableNotifications: 'Mute Project',
  /** Menu item to unmute project notifications */
  enableNotifications: 'Unmute Project',
  /** Menu item to open link in a new tab */
  openInNewTab: 'Open in New Tab',
});

const ACTION_CONTEXT: NotificationsActionContext = 'context-menu';

export function ContextMenuContent({
  projectId,
  updateNotificationsReadStatus,
  href,
  isRead,
  onNewTabLinkClick,
}: {
  projectId?: string;
  updateNotificationsReadStatus: ({
    context,
  }: NotificationsActionCallback) => void;
  href: string;
  isRead: boolean;
  onNewTabLinkClick: ({ context }: NotificationsActionCallback) => void;
}) {
  const { data: notificationsStatus, complete } =
    useProjectNotificationsStatusFragment({
      from: projectId
        ? {
            __typename: 'Project',
            id: projectId,
          }
        : null,
    });

  const [
    localProjectNotificationsStatusState,
    updateLocalProjectNotificationsStatusState,
  ] = useState(complete ? notificationsStatus : null);

  const {
    enableProjectNotificationsMutation,
    disableProjectNotificationsMutation,
  } = useToggleProjectNotifications({
    projectId: projectId ?? '',
    context: ACTION_CONTEXT,
  });

  useEffect(
    function maybeUpdateLocalProjectNotificationsStatusState() {
      if (localProjectNotificationsStatusState === null && complete) {
        updateLocalProjectNotificationsStatusState(notificationsStatus);
      }
    },
    [
      localProjectNotificationsStatusState,
      updateLocalProjectNotificationsStatusState,
      complete,
      notificationsStatus,
    ]
  );

  const localIsRead = useRef(isRead);

  const muteButtonProps =
    localProjectNotificationsStatusState?.notificationsEnabled
      ? {
          icon: <IconNotificationsDisabled />,
          onClick: disableProjectNotificationsMutation,
          label: TEXT.disableNotifications,
        }
      : {
          icon: <IconNotifications />,
          onClick: enableProjectNotificationsMutation,
          label: TEXT.enableNotifications,
        };

  return (
    <MenuGroup>
      <MenuButton
        data-testid="notification-cell-context-menu--toggle-read"
        iconBefore={<IconMailRead />}
        onClick={() =>
          updateNotificationsReadStatus({ context: ACTION_CONTEXT })
        }
      >
        {localIsRead.current ? TEXT.markAsUnread : TEXT.markAsRead}
      </MenuButton>
      {projectId && (
        <MenuButton
          data-testid="notification-cell-context-menu--toggle-project-notifications"
          iconBefore={muteButtonProps.icon}
          onClick={muteButtonProps.onClick}
        >
          {muteButtonProps.label}
        </MenuButton>
      )}
      <MenuSeparator />
      <MenuLink
        data-testid="notification-cell-context-menu--open-in-new-tab"
        href={href}
        iconBefore={<IconCustomFieldLink />}
        onClick={() => onNewTabLinkClick({ context: ACTION_CONTEXT })}
        rel="noopener noreferrer"
        target="_blank"
      >
        {TEXT.openInNewTab}
      </MenuLink>
    </MenuGroup>
  );
}
